import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import { useGetMorphemePanelsSlicesQueries } from "@-/dev.improve.haniverse.lib.morpheme.read";
import type { PageNavigatorOptions } from "@-/dev.improve.haniverse.lib.navigator.page";
import { drillTypeCodec } from "@-/dev.improve.haniverse.lib.drill.entities";
import {
  useGetReviewCardFsrsResolutionQueryKey,
  reviewCardFsrsResolutionQueryFn,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data/use-review-card-fsrs-resolution-query";
import {
  useGetWordCharacterBacksQueryKey,
  useGetWordContextSentencesQueryKey,
  wordCharacterBacksQueryFn,
  wordContextSentencesQueryFn,
} from "@-/dev.improve.haniverse.lib.sentence.context";
import { queryOptions } from "@-/com.npmjs.tanstack__react-query";
import { Platform } from "@-/com.npmjs.react-native";
import { useCallback } from "react";

type MorphemePanelRouteSegment = "info" | "mnemonics";

const MORPHEME_PANEL_SEGMENT_SUFFIX_REGEX = /\/(?:mnemonics|info)$/;
const SEARCH_PARAM_VIEW_HREF_SUFFIX = "/morpheme";

/**
 * The card route families. Each mounts both panels (Learn / Details) on its single `(panels)/info`
 * route and selects one with the `view` search param, so they render the same page.
 */
const CARD_PANELS_HREF_LIST = [
  "/(nav)/(app)/browse/morpheme/[morpheme]",
  "/(nav)/(app)/search/card/[morpheme]",
  "/(nav)/(app)/decks/morpheme/[morpheme]",
  "/(nav)/(app)/collections/morpheme/[morpheme]",
  "/(nav)/(app)/learn/explore/morpheme/[morpheme]",
  "/(nav)/(app)/learn/map/morpheme/[morpheme]",
] as const;

function cardPanelsHrefForPrefix(href: MorphemeHrefObject["href"]) {
  return CARD_PANELS_HREF_LIST.find(
    (cardPanelsHref) => href === cardPanelsHref || href.startsWith(`${cardPanelsHref}/`),
  );
}

function normalizeRouteParam(value: string | string[] | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return Array.isArray(value) ? value[0] : value;
}

function routeSegmentForPrefix(prefix: MorphemeHrefObject): MorphemePanelRouteSegment {
  const view = normalizeRouteParam(prefix.params.view);
  if (view === "mnemonics" || view === "info") {
    return view;
  }
  if (prefix.href.endsWith("/mnemonics")) {
    return "mnemonics";
  }
  return "info";
}

function hrefForRouteSegment(
  href: MorphemeHrefObject["href"],
  segment: MorphemePanelRouteSegment,
): MorphemeHrefObject["href"] {
  if (href.endsWith(SEARCH_PARAM_VIEW_HREF_SUFFIX)) {
    return href;
  }
  return `${href.replace(MORPHEME_PANEL_SEGMENT_SUFFIX_REGEX, "")}/${segment}`;
}

/**
 * The bare (panel-less) href, which renders the single-scroll Reference view.
 *
 * Only browse-side navigators opt into this, and only on web: Reference is the browse presentation
 * of a grapheme. Native Browse uses a combined detail panel. The bare route is still bundled on
 * native for Expo Router parity, but its index redirects before Reference can mount.
 */
function hrefWithoutRouteSegment(href: MorphemeHrefObject["href"]): MorphemeHrefObject["href"] {
  if (href.endsWith(SEARCH_PARAM_VIEW_HREF_SUFFIX)) {
    return href;
  }
  return href.replace(MORPHEME_PANEL_SEGMENT_SUFFIX_REGEX, "") as MorphemeHrefObject["href"];
}

export function useMorphemePanelsPageNavigatorOptions() {
  const getMorphemePanelsSlicesQueries = useGetMorphemePanelsSlicesQueries();
  const getReviewCardFsrsResolutionQueryKey = useGetReviewCardFsrsResolutionQueryKey();
  const getWordCharacterBacksQueryKey = useGetWordCharacterBacksQueryKey();
  const getWordContextSentencesQueryKey = useGetWordContextSentencesQueryKey();

  return useCallback(
    ({
      morpheme,
      prefer,
      prefix,
    }: {
      morpheme: string;
      // `"reference"` sends web navigation to the bare href (the single-scroll Reference view)
      // instead of a panel segment. Ignored on native, which uses tabs; see
      // `hrefWithoutRouteSegment`. Defaults to the panel behaviour for every other navigator.
      prefer?: "panels" | "reference";
      prefix: MorphemeHrefObject;
    }): PageNavigatorOptions => {
      const routeSegment = routeSegmentForPrefix(prefix);
      const shouldPreferReference = prefer === "reference" && Platform.OS === "web";
      const cardPanelsHref = cardPanelsHrefForPrefix(prefix.href);
      const pathname = shouldPreferReference
        ? hrefWithoutRouteSegment(prefix.href)
        : cardPanelsHref === undefined
          ? hrefForRouteSegment(prefix.href, routeSegment)
          : (`${cardPanelsHref}/info` as const);

      // Strip any inherited `morpheme` (e.g. the source route's own param leaked in via raw
      // useLocalSearchParams) so the navigation target always wins; stack segments
      // (`type`) and intentional query params pass through. Card routes carry the panel as the
      // `view` search param; other panel routes represent it as a path segment, while lesson panel
      // routes already end in `/morpheme` and keep `view` as a search param for their internal
      // tab state.
      //
      // A Reference target drops `view` too: it renders no panels, so a carried-over
      // `?view=mnemonics` would be dead weight in the URL (and `pathname === prefix.href` is
      // trivially true for an already-bare prefix, which would otherwise preserve it).
      const { morpheme: _sourceMorpheme, view, ...restParams } = prefix.params;
      const params =
        cardPanelsHref !== undefined && !shouldPreferReference
          ? { ...restParams, morpheme, view: routeSegment }
          : !shouldPreferReference && pathname === prefix.href
            ? { ...restParams, morpheme, view }
            : { ...restParams, morpheme };

      const {
        infoGraphemeMorphemeSliceQueryOptions,
        infoWordMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeSliceQueryOptions,
        mnemonicsPanelMorphemeUserSliceQueryOptions,
      } = getMorphemePanelsSlicesQueries(morpheme);

      // A word-sentence reveal lands on a DIFFERENT layout than the panel pair
      // (`ReviewCardWordSentenceLayoutSuspense`, branched in `review-card-morpheme-layout.tsx`),
      // and it reads a disjoint query set: fsrs resolution + the two word queries, and NONE of the
      // panel slices. Prefetching panel slices for it warms three keys it never reads while leaving
      // all three it does read cold, so the reveal always suspends. The drill type rides the `type`
      // param the reveal encodes; a route without one decodes to `meaning` and takes the panel path.
      const drillType = drillTypeCodec.decode(normalizeRouteParam(prefix.params.type));
      if (drillType.kind === "word-sentence") {
        return {
          href: { params, pathname },
          queries: [
            queryOptions({
              queryKey: getReviewCardFsrsResolutionQueryKey(morpheme, drillType),
              queryFn: reviewCardFsrsResolutionQueryFn,
            }),
            queryOptions({
              queryKey: getWordCharacterBacksQueryKey(morpheme),
              queryFn: wordCharacterBacksQueryFn,
            }),
            queryOptions({
              queryKey: getWordContextSentencesQueryKey(morpheme),
              queryFn: wordContextSentencesQueryFn,
            }),
          ],
        };
      }

      return {
        href: {
          params,
          pathname,
        },
        queries:
          // Reference renders the mnemonics AND info screens in one scroll, so it needs the full
          // set — the same one the info panel prefetches, which already includes both mnemonics
          // slices for the shared toolbar/header.
          routeSegment === "mnemonics" && !shouldPreferReference
            ? [mnemonicsPanelMorphemeSliceQueryOptions, mnemonicsPanelMorphemeUserSliceQueryOptions]
            : [
                infoGraphemeMorphemeSliceQueryOptions,
                infoWordMorphemeSliceQueryOptions,
                mnemonicsPanelMorphemeSliceQueryOptions,
                mnemonicsPanelMorphemeUserSliceQueryOptions,
              ],
      };
    },
    [
      getMorphemePanelsSlicesQueries,
      getReviewCardFsrsResolutionQueryKey,
      getWordCharacterBacksQueryKey,
      getWordContextSentencesQueryKey,
    ],
  );
}
